import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarRange, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type DataTableColumnDef,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { AdminAuctionSale } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Tabs, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { AuctionsNav } from '../components/auctions-nav';
import { SaleCreateDialog } from '../components/sale-create-dialog';
import { SaleStatusBadge } from '../components/sale-status-badge';
import { useSales } from '../hooks/use-sales';
import { saleTab } from '../schemas/sale-list-search';
import { SALE_TABS, type SaleTab } from '../schemas/sale-rules';

const ROUTE_ID = '/_authed/_active/s/$storeId/auctions/sales/' as const;
const ROUTE_PATH = '/s/$storeId/auctions/sales/' as const;

function Sales() {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [creating, setCreating] = useState(false);
  const tab = saleTab(search);

  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange });
  const query = useSales(store.id, { ...list.params, status: tab });

  const columns = useMemo<Array<DataTableColumnDef<AdminAuctionSale>>>(
    () => [
      {
        id: 'sale',
        header: t('sales.columns.sale'),
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <Link
              to="/s/$storeId/auctions/sales/$saleId"
              params={{ storeId: store.id, saleId: row.original.id }}
              className="truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.title}
            </Link>
            <span className="text-muted-foreground truncate text-xs">
              {row.original.subtitle ?? `/sales/${row.original.handle}`}
            </span>
          </span>
        ),
        meta: { label: t('sales.columns.sale'), lockVisibility: true },
        size: 320,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <SaleStatusBadge status={row.original.status} />,
        size: 110,
      },
      {
        id: 'lots',
        header: t('sales.columns.lots'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.status === 'ended'
              ? t('sales.soldOf', { sold: row.original.soldCount, count: row.original.lotCount })
              : row.original.lotCount}
          </span>
        ),
        meta: { label: t('sales.columns.lots'), align: 'end' },
        size: 110,
      },
      {
        id: 'hammer',
        header: t('sales.columns.hammerTotal'),
        cell: ({ row }) =>
          row.original.hammerTotal.amount > 0 ? (
            <span className="font-medium tabular-nums">
              {format.money(row.original.hammerTotal)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('none')}</span>
          ),
        meta: { label: t('sales.columns.hammerTotal'), align: 'end' },
        size: 140,
      },
      {
        id: 'when',
        header: t('sales.columns.when'),
        cell: ({ row }) => {
          const sale = row.original;
          const key =
            sale.status === 'draft' || sale.status === 'scheduled'
              ? 'sales.when.opens'
              : sale.status === 'live'
                ? 'sales.when.firstClose'
                : sale.status === 'ended'
                  ? 'sales.when.ended'
                  : 'sales.when.cancelled';
          const at =
            sale.status === 'draft' || sale.status === 'scheduled'
              ? sale.startsAt
              : sale.status === 'live'
                ? sale.firstCloseAt
                : (sale.endedAt ?? sale.cancelledAt ?? sale.lastCloseAt ?? sale.firstCloseAt);
          return <span title={format.utc(at)}>{t(key, { when: format.dateTime(at) })}</span>;
        },
        meta: { label: t('sales.columns.when') },
        size: 220,
      },
    ],
    [format, store.id, t],
  );

  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    getRowId: (row) => row.id,
    tableId: 'auction-sales',
  });
  const isEmpty = query.isSuccess && query.data.items.length === 0 && !list.cursor;

  const newButton = editable ? (
    <Button onClick={() => setCreating(true)} data-tour="auctions.sales.new">
      <Plus aria-hidden />
      {t('sales.actions.new')}
    </Button>
  ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('sales.title')}
        description={t('sales.description')}
        actions={newButton}
      />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <AuctionsNav storeId={store.id} />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              status: value === 'live' ? undefined : (value as SaleTab),
              cursor: undefined,
            }),
          })
        }
        className="mb-4"
      >
        <TabsList aria-label={t('sales.tabs.label')}>
          {SALE_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`sales.status.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {isEmpty ? (
        <EmptyState
          icon={CalendarRange}
          title={t(`sales.empty.${tab}.title`)}
          description={t(`sales.empty.${tab}.description`)}
          action={tab === 'draft' || tab === 'live' || tab === 'scheduled' ? newButton : undefined}
        />
      ) : (
        <DataTable
          table={table}
          toolbar={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/auctions/sales/$saleId',
              params: { storeId: store.id, saleId: row.id },
            })
          }
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('sales.resource.one'), plural: t('sales.resource.other') }}
          emptyState={{ title: t('noMatches') }}
        />
      )}
      {editable ? (
        <SaleCreateDialog
          storeId={store.id}
          timeZone={displayTimeZone(store.timezone)}
          open={creating}
          onOpenChange={setCreating}
          onCreated={(sale) =>
            void navigate({
              to: '/s/$storeId/auctions/sales/$saleId',
              params: { storeId: store.id, saleId: sale.id },
            })
          }
        />
      ) : null}
    </PageContent>
  );
}

export function SalesPage() {
  return (
    <FeatureErrorBoundary name="auctions.sales">
      <Sales />
    </FeatureErrorBoundary>
  );
}
